/**
 * sl-yphr: ELSTER Block Schema
 *
 * Defines the data model for ELSTER blocks that handle UStVA
 * (Umsatzsteuervoranmeldung) filings on the edgeless canvas.
 *
 * Architecture:
 * - Input Edge: Database with UStVA Kennzahlen (rows=months, cols=25 Kz)
 * - History: Sealed in Prisma ElsterFiling table (sl-wlcm)
 * - Config: Steuernummer + Bundesland from Workspace ERiCA Config
 * - Filing: Via ERiCA sidecar -> ELSTER Clearingstelle
 *
 * Follows the Note-Reduce Pattern for correct canvas positioning.
 */

import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import type { Text } from '@blocksuite/store';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

import type { ElsterFilingType } from './elster-types.js';

// =============================================================================
// Edgeless Props (Note-Reduce Pattern)
// =============================================================================

export type ElsterEdgelessProps = {
  style: {
    borderRadius: number;
    borderSize: number;
  };
  scale?: number;
  collapse?: boolean;
  collapsedHeight?: number;
};

// =============================================================================
// Props Type
// =============================================================================

export type ElsterBlockProps = {
  // Meta
  title: Text;
  filingType: ElsterFilingType;

  // Input Edge: UStVA Kennzahlen Database
  inputTableId: string | null;
  inputTableName: string | null;
  inputDocId: string | null;

  // Selected row index in the input database (which month to file)
  selectedRowIndex: number | null;

  // Schema version (set at provisioning, used for migration detection)
  schemaVersion: string | null;

  // Edgeless Props (Note-Reduce Pattern)
  edgeless: ElsterEdgelessProps;
} & GfxCompatibleProps;

// =============================================================================
// Model Class
// =============================================================================

export class ElsterBlockModel extends GfxCompatible<ElsterBlockProps>(
  BlockModel
) {
  /** Check if the block has an input database connected */
  get hasInput(): boolean {
    return this.props.inputTableId !== null;
  }
}

// =============================================================================
// Default Values
// =============================================================================

export const DEFAULT_ELSTER_WIDTH = 640;
export const DEFAULT_ELSTER_HEIGHT = 200;
const DEFAULT_BORDER_RADIUS = 8;
const DEFAULT_BORDER_SIZE = 1;

// =============================================================================
// Schema Definition
// =============================================================================

export const ElsterBlockSchema = defineBlockSchema({
  flavour: 'affine:elster',
  props: (internal): ElsterBlockProps => ({
    // Meta
    title: internal.Text('UStVA'),
    filingType: 'ustva',

    // Input Edge
    inputTableId: null,
    inputTableName: null,
    inputDocId: null,

    // Selected row
    selectedRowIndex: null,

    // Schema version
    schemaVersion: null,

    // GfxCompatible props
    xywh: `[0,0,${DEFAULT_ELSTER_WIDTH},${DEFAULT_ELSTER_HEIGHT}]`,
    index: 'a0',
    lockedBySelf: false,

    // Edgeless Props (Note-Reduce Pattern)
    edgeless: {
      style: {
        borderRadius: DEFAULT_BORDER_RADIUS,
        borderSize: DEFAULT_BORDER_SIZE,
      },
    },
  }),
  metadata: {
    role: 'hub',
    version: 1,
    parent: ['affine:note', '@root'],
    children: [],
  },
  toModel: () => new ElsterBlockModel(),
});

// =============================================================================
// Schema Extension
// =============================================================================

export const ElsterBlockSchemaExtension =
  BlockSchemaExtension(ElsterBlockSchema);
